import { DialogComponent } from '@theme/dialog';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, StorePreferenceChangedEvent } from '@theme/events';
import { STORE_PREFERENCE_EVENT } from '@theme/store-routes';

/**
 * Slim store selector drawer.
 * Handles search/filter, selection, assign endpoint + cart attr sync, localStorage sync, and header label update.
 */
class StoreSelectorDrawerComponent extends DialogComponent {
  static EVENT_NAME = STORE_PREFERENCE_EVENT;
  static CONNECTED_LOCATION_ID = 'data-connected-location-id';
  static CONNECTED_LOCATION_NAME = 'data-connected-location-name';
  /**
   * Single cart attribute that holds all store-selection data as a flat JSON object.
   * Read on the storefront by `cart-info-field.liquid` and at checkout by the
   * selected-store pickup-generator Shopify Function.
   */
  static CART_INFO_KEY = '_cart_info';
  static CART_INFO_VERSION = 1;
  /**
   * Attribute keys written by the previous multi-attribute format. Cleared on every
   * write so a cart never carries stale store data alongside `_cart_info`.
   */
  static LEGACY_CART_ATTR_KEYS = [
    'connected_location_id',
    'connected_location_name',
    'location_address',
    'location_phone',
    'location_email',
    'location_monday',
    'location_tuesday',
    'location_wednesday',
    'location_thursday',
    'location_friday',
    'location_saturday',
    'location_sunday',
  ];
  static COMPANY_LOCATION_ID = 'data-company-location-id';
  static CART_CONNECTED_LOCATION_ID = 'data-cart-connected-location-id';
  static CART_CONNECTED_LOCATION_NAME = 'data-cart-connected-location-name';

  #isSwitching = false;
  /** @type {{ id: string, name: string } | null} */
  #pendingSelection = null;
  /** @type {{ id: string, name: string } | null} */
  #pendingPrevious = null;
  /** @type {HTMLElement | null} */
  #pageOverlay = null;
  /** @type {HTMLButtonElement | null} */
  #activeButton = null;
  /** @type {number} Counter to track selection version and ignore stale async responses */
  #selectionVersion = 0;
  /** @type {boolean} Whether lazy content has been loaded */
  #contentLoaded = false;
  /** @type {boolean} Whether lazy content is currently loading */
  #loadingContent = false;

  connectedCallback() {
    super.connectedCallback();

    // Check if content is already loaded (from data attribute)
    this.#contentLoaded = this.dataset.contentLoaded === 'true';

    // Always register dialog:open listener for lazy loading
    this.addEventListener('dialog:open', this.#handleDialogOpen);

    if (!this.#canCustomerShop()) {
      void this.#clearStorePreference();
      return;
    }
    if (this.hasAttribute('force-dialog')) {
      const selectedLocationId = this.connectedLocationId;
      if (selectedLocationId) {
        this.removeAttribute('force-dialog');
      } else {
        this.showDialog();
      }
      return;
    }
    this.#syncInitialSelectionFromCartAttributes();

    // Sync company location metafields to cart attributes if cart attributes are empty
    void this.#syncCompanyLocationToCartAttributes();
  }

  /**
   * Handle dialog open - load content lazily if needed
   */
  #handleDialogOpen = async () => {
    // Load lazy content if not already loaded
    if (!this.#contentLoaded && !this.#loadingContent) {
      await this.#loadStoreContent();
    }

    this.clearSearch();
    const input = this.querySelector('.store-selector-drawer-search-input');
    if (input instanceof HTMLInputElement) input.focus();
  };

  /**
   * Lazy loads the store list content via Section Rendering API
   */
  async #loadStoreContent() {
    const lazyLoadUrl = this.dataset.lazyLoadUrl;
    if (!lazyLoadUrl) {
      console.warn('[StoreSelectorDrawer] No lazy load URL configured');
      return;
    }

    this.#loadingContent = true;
    const skeleton = this.querySelector('[ref="loadingSkeleton"]');
    const container = this.querySelector('[ref="storesContainer"]');

    try {
      const url = new URL(lazyLoadUrl, window.location.origin);

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`Failed to load store content: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const content = doc.getElementById('store-selector-lazy-content');

      if (content && container) {
        // Hide skeleton
        if (skeleton instanceof HTMLElement) skeleton.hidden = true;

        // Insert content
        container.innerHTML = content.innerHTML;

        // Mark as loaded
        this.#contentLoaded = true;
        this.dataset.contentLoaded = 'true';

        // Re-initialize refs for the loaded content
        this.#initializeLoadedContent();
      }
    } catch (error) {
      console.error('[StoreSelectorDrawer] Failed to load store content:', error);
      // Show error state
      if (container) {
        container.innerHTML = `
          <div class="store-selector-drawer-error">
            <p>Failed to load stores. Please try again.</p>
            <button class="button button-secondary" type="button" on:click="/retryLoad">
              Retry
            </button>
          </div>
        `;
      }
    } finally {
      this.#loadingContent = false;
    }
  }

  /**
   * Retry loading content after failure
   */
  retryLoad = () => {
    const skeleton = this.querySelector('[ref="loadingSkeleton"]');
    const container = this.querySelector('[ref="storesContainer"]');

    if (skeleton instanceof HTMLElement) {
      skeleton.hidden = false;
    }

    if (container) {
      container.innerHTML = '';
      if (skeleton) container.appendChild(skeleton);
    }

    this.#loadStoreContent();
  };

  /**
   * Initialize refs and event handlers for dynamically loaded content
   */
  #initializeLoadedContent() {
    // Update refs for lazy-loaded elements
    const storesList = this.querySelector('[ref="storesList"]');
    if (storesList) {
      this.refs.storesList = storesList;
    }

    const storesEmpty = this.querySelector('[ref="storesEmpty"]');
    if (storesEmpty) {
      this.refs.storesEmpty = storesEmpty;
    }
  }

  /**
   * Called after the section is morphed by the Section Rendering API.
   * Re-syncs internal state so lazy-loaded content isn't lost.
   */
  updatedCallback() {
    super.updatedCallback();

    // The morph resets data-content-loaded to "false" from server HTML.
    // Restore it if content was already loaded so we don't re-fetch.
    if (this.#contentLoaded) {
      this.dataset.contentLoaded = 'true';
    }

    // The morph may re-add force-dialog from server HTML even after a store was selected.
    if (this.hasAttribute('force-dialog') && this.connectedLocationId) {
      this.removeAttribute('force-dialog');
    }
  }

  #canCustomerShop() {
    return this.dataset.shoppingIsBlocked !== 'true';
  }

  async #clearStorePreference() {
    this.removeAttribute(StoreSelectorDrawerComponent.CONNECTED_LOCATION_ID);
    this.removeAttribute(StoreSelectorDrawerComponent.CONNECTED_LOCATION_NAME);
    try {
      await this.#setCartAttributes('', '', '');
    } catch (error) {
      console.error('Failed to clear store preference', error);
    }
  }

  // Aliases for reuse
  open() {
    this.showDialog();
  }
  close() {
    return this.closeDialog();
  }
  toggle() {
    this.toggleDialog();
  }

  get connectedLocationId() {
    // Prioritize cart attributes (updated by JS) over company metafields (stale until page reload)
    return this.#getConnectedLocationIdFromCartAttributes() || this.#getConnectedLocationIdFromCompanyLocation() || null;
  }

  get connectedLocationName() {
    // Prioritize cart attributes (updated by JS) over company metafields (stale until page reload)
    return this.#getConnectedLocationNameFromCartAttributes() || this.#getConnectedLocationNameFromCompanyLocation() || null;
  }

  #getCurrentSelection() {
    const id = this.connectedLocationId;
    if (!id) return null;
    return {
      id,
      name: this.connectedLocationName || '',
    };
  }

  #setPendingSelection(nextSelection, previousSelection) {
    this.#pendingSelection = nextSelection || null;
    this.#pendingPrevious = previousSelection || null;
  }

  #resetPendingSelection() {
    this.#pendingSelection = null;
    this.#pendingPrevious = null;
  }

  /**
   * Handle selecting a store.
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  async selectStore(event) {
    if (this.#isSwitching) return;

    const targetItem = event?.target?.closest?.('[data-location-id]');
    if (!(targetItem instanceof HTMLElement)) return;

    const locationId = targetItem.getAttribute('data-location-id');
    const locationName = targetItem.getAttribute('data-location-name') || '';
    const locationDetails = this.#getLocationDetailsFromElement(targetItem);
    if (!locationId) return;
    if (locationId === this.connectedLocationId) return;

    // Track the clicked button for loading state
    const clickedButton = targetItem.querySelector('[ref="selectStoreButton"]');
    if (clickedButton instanceof HTMLButtonElement) {
      this.#activeButton = clickedButton;
    }

    const nextSelection = { id: locationId, name: locationName, details: locationDetails };
    const previousSelection = this.#getCurrentSelection();

    if (await this.#shouldConfirmSwitch(locationId, previousSelection)) {
      this.#showConfirmation(targetItem, nextSelection, previousSelection);
      return;
    }

    await this.#commitSelection(nextSelection, previousSelection);
  }

  async #commitSelection(nextSelection, previousSelection) {
    if (!nextSelection?.id) return;

    // Increment version to invalidate any in-flight requests from previous selections
    const currentVersion = ++this.#selectionVersion;

    this.setLoading(true);
    this.#showPageLoadingOverlay();
    if (this.hasAttribute('force-dialog')) this.removeAttribute('force-dialog');
    void this.close();
    try {
      // Clear cart first before any other operations
      await this.#clearCartOnStoreChange(nextSelection.id, previousSelection);

      // Check if a newer selection was started - if so, abort this one
      if (currentVersion !== this.#selectionVersion) return;

      if (this.#getUserAssignedCompanyLocation()) {
        await this.#assignCatalogToCompanyLocation(nextSelection.id);
      }

      // Check again after async operation
      if (currentVersion !== this.#selectionVersion) return;

      await this.#setCartAttributes(nextSelection.id, nextSelection.name, nextSelection.details || {});

      // Final check before applying UI updates
      if (currentVersion !== this.#selectionVersion) return;

      this.updateConnectedLocationNameInHeader(nextSelection.name);
      this.#updateSelectedStoreInList(nextSelection.id);
      this.#updateUrlWithNewStore(nextSelection.name);
      this.#updateLinksWithNewStore(nextSelection.name);

      document.dispatchEvent(
        new StorePreferenceChangedEvent({
          id: nextSelection.id,
          name: nextSelection.name,
          details: nextSelection.details || {},
        }),
      );
    } catch (error) {
      // Only log error if this is still the current selection
      if (currentVersion === this.#selectionVersion) {
        console.error('Failed to change store', error);
        this.#showErrorMessage('Failed to switch store. Please try again.');
      }
      throw error;
    } finally {
      // Only reset UI if this is still the current selection
      if (currentVersion === this.#selectionVersion) {
        this.#resetPendingSelection();
        this.setLoading(false);
        this.#hidePageLoadingOverlay();
      }
    }
  }

  /**
   * @param {string} [name]
   */
  updateConnectedLocationNameInHeader(name) {
    const triggerText = this.querySelector('.store-selector-drawer-trigger-text');
    if (triggerText) {
      triggerText.textContent = name || this.connectedLocationName;
    }
  }

  /**
   * Updates the URL to reflect the new store selection.
   * Finds and replaces any existing store name in the URL path.
   * @param {string} newStoreName - The name of the newly selected store.
   */
  #updateUrlWithNewStore(newStoreName) {
    if (!newStoreName) return;

    const url = new URL(window.location.href);
    const pathParts = url.pathname.split('/').filter(Boolean);
    const isCollectionOrSearch = pathParts.includes('collections') || pathParts.includes('search');

    // Only handle URL updates for collection/search pages
    if (!isCollectionOrSearch) return;

    // Get all known store names from the drawer list
    /** @type {string[]} */
    const storeNames = this.#getStoreNamesFromList();

    // Find if any store name is in the current URL path (after collections/search segment)
    const collectionIndex = pathParts.findIndex((p) => p === 'collections' || p === 'search');
    if (collectionIndex === -1) return;

    // Look for store name in path parts after the collection/search handle
    for (let i = collectionIndex + 2; i < pathParts.length; i++) {
      const decodedPart = decodeURIComponent(pathParts[i]);
      const matchingStore = storeNames.find((name) => name && name.toLowerCase() === decodedPart.toLowerCase());
      if (matchingStore) {
        // Replace existing store name with new one
        pathParts[i] = encodeURIComponent(newStoreName);
        url.pathname = '/' + pathParts.join('/');
        history.replaceState({}, '', url.toString());
        return;
      }
    }
  }

  #updateLinksWithNewStore(newStoreName) {
    if (!newStoreName) return;

    /** @type {string[]} */
    const storeNames = this.#getStoreNamesFromList();
    if (storeNames.length === 0) return;

    const links = document.querySelectorAll('a[href]');
    links.forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      const rawHref = link.getAttribute('href');
      if (!rawHref || rawHref.startsWith('#') || rawHref.startsWith('mailto:') || rawHref.startsWith('tel:')) return;

      let url;
      try {
        url = new URL(rawHref, window.location.origin);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) return;

      const pathParts = url.pathname.split('/').filter(Boolean);
      const isCollectionOrSearch = pathParts.includes('collections') || pathParts.includes('search');
      if (!isCollectionOrSearch) return;

      const collectionIndex = pathParts.findIndex((part) => part === 'collections' || part === 'search');
      if (collectionIndex === -1) return;

      for (let i = collectionIndex + 2; i < pathParts.length; i++) {
        const decodedPart = decodeURIComponent(pathParts[i]);
        const matchingStore = storeNames.find((name) => name && name.toLowerCase() === decodedPart.toLowerCase());
        if (matchingStore) {
          pathParts[i] = encodeURIComponent(newStoreName);
          url.pathname = '/' + pathParts.join('/');
          const nextHref = /^https?:\/\//i.test(rawHref) ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
          link.setAttribute('href', nextHref);
          return;
        }
      }
    });
  }

  #getStoreNamesFromList() {
    return Array.from(this.querySelectorAll('[data-location-name]'))
      .map((el) => el.getAttribute('data-location-name'))
      .filter(/** @returns {name is string} */ (name) => Boolean(name));
  }

  /**
   * Updates the selected state of store items in the drawer list.
   * @param {string} selectedId - The ID of the newly selected store.
   */
  #updateSelectedStoreInList(selectedId) {
    const items = this.querySelectorAll('.store-selector-drawer-item');
    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const itemId = item.getAttribute('data-location-id');
      const isSelected = itemId === selectedId;

      item.classList.toggle('is-selected', isSelected);
      item.setAttribute('aria-current', isSelected ? 'true' : 'false');

      const button = item.querySelector('[ref="selectStoreButton"]');
      if (button instanceof HTMLButtonElement) {
        button.disabled = isSelected;
        button.classList.toggle('button-primary', !isSelected);
        button.classList.toggle('button-secondary', isSelected);

        const label = button.querySelector('.store-selector-drawer-item-cta-label');
        if (label) {
          label.textContent = isSelected ? this.dataset.ctaSelectedLabel || 'Valgt' : this.dataset.ctaChooseLabel || 'Vælg lokation';
        }
      }
    });
  }

  #getConnectedLocationIdFromCartAttributes() {
    return this.dataset.cartConnectedLocationId || this.getAttribute(StoreSelectorDrawerComponent.CART_CONNECTED_LOCATION_ID) || null;
  }

  #getConnectedLocationNameFromCartAttributes() {
    return this.dataset.cartConnectedLocationName || this.getAttribute(StoreSelectorDrawerComponent.CART_CONNECTED_LOCATION_NAME) || null;
  }

  #getLocationDetailsFromElement(targetItem) {
    const openingHours = {};

    if (targetItem.dataset.locationMonday) openingHours.monday = targetItem.dataset.locationMonday;
    if (targetItem.dataset.locationTuesday) openingHours.tuesday = targetItem.dataset.locationTuesday;
    if (targetItem.dataset.locationWednesday) openingHours.wednesday = targetItem.dataset.locationWednesday;
    if (targetItem.dataset.locationThursday) openingHours.thursday = targetItem.dataset.locationThursday;
    if (targetItem.dataset.locationFriday) openingHours.friday = targetItem.dataset.locationFriday;
    if (targetItem.dataset.locationSaturday) openingHours.saturday = targetItem.dataset.locationSaturday;
    if (targetItem.dataset.locationSunday) openingHours.sunday = targetItem.dataset.locationSunday;

    return {
      address: targetItem.dataset.locationAddress || '',
      phone: targetItem.dataset.locationPhone || '',
      email: targetItem.dataset.locationEmail || '',
      opening_hours: openingHours,
    };
  }

  #getConnectedLocationIdFromCompanyLocation() {
    return Theme?.customer?.company?.current_location?.metafields?.connected_location_id || null;
  }

  #getConnectedLocationNameFromCompanyLocation() {
    return Theme?.customer?.company?.current_location?.metafields?.connected_location_name || null;
  }

  #getUserAssignedCompanyLocation() {
    return this.dataset.companyLocationId || this.getAttribute(StoreSelectorDrawerComponent.COMPANY_LOCATION_ID) || null;
  }

  #syncInitialSelectionFromCartAttributes() {
    const connectedLocationId = this.connectedLocationId;
    if (connectedLocationId) {
      this.setAttribute(StoreSelectorDrawerComponent.CONNECTED_LOCATION_ID, connectedLocationId);
    } else {
      this.removeAttribute(StoreSelectorDrawerComponent.CONNECTED_LOCATION_ID);
    }

    const connectedLocationName = this.connectedLocationName;
    if (connectedLocationName) {
      this.setAttribute(StoreSelectorDrawerComponent.CONNECTED_LOCATION_NAME, connectedLocationName);
    } else {
      this.removeAttribute(StoreSelectorDrawerComponent.CONNECTED_LOCATION_NAME);
    }
  }

  /**
   * Syncs company location metafields to cart attributes if cart attributes are empty or missing details.
   * This handles the case where cart attributes are cleared but company location still has the store preference.
   * The Function Studio app at checkout relies on cart attributes, so we need to ensure they're populated.
   */
  async #syncCompanyLocationToCartAttributes() {
    // Get the connected location ID (prioritize cart attributes, fall back to company metafields)
    const connectedLocationId = this.connectedLocationId;
    const connectedLocationName = this.connectedLocationName;

    if (!connectedLocationId) return;

    // Check if location details are missing from cart attributes (only ID/name present)
    const hasLocationDetails = this.dataset.selectedLocationAddress || this.dataset.selectedLocationPhone || this.dataset.selectedLocationEmail;

    // If location ID exists but details are missing, sync the full details to cart
    if (!hasLocationDetails) {
      try {
        // Ensure store content is loaded so we can get location details from DOM
        if (!this.#contentLoaded && !this.#loadingContent) {
          await this.#loadStoreContent();
        }

        // Find the location details from the store list in the DOM
        const locationDetails = this.#getLocationDetailsById(connectedLocationId);
        await this.#setCartAttributes(connectedLocationId, connectedLocationName || '', locationDetails);
      } catch (error) {
        console.error('[StoreSelectorDrawer] Failed to sync location details to cart:', error);
      }
    }
  }

  /**
   * Gets location details from the store list by location ID
   * @param {string} locationId - The location ID to find
   * @returns {object} The location details (address, phone, email, opening hours)
   */
  #getLocationDetailsById(locationId) {
    const storeItem = this.querySelector(`[data-location-id="${locationId}"]`);
    if (!storeItem) {
      console.warn('[StoreSelectorDrawer] Could not find store item in DOM for location ID:', locationId);
      return {};
    }
    return this.#getLocationDetailsFromElement(storeItem);
  }

  async #assignCatalogToCompanyLocation(locationId) {
    const companyLocationId = this.#getUserAssignedCompanyLocation();
    if (!companyLocationId) throw new Error('Missing company location id');

    const url = `/apps/products/api/company-locations/${companyLocationId}/assign-store/${locationId}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const message = await response.text().catch(() => response.statusText);
      throw new Error(message || `Failed to assign store (${response.status})`);
    }

    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * Builds the compact `_cart_info` payload. Keep this shape flat: the storefront
   * reader (`cart-info-field.liquid`) and the notification templates parse it without
   * a real JSON parser, so nested objects would not be readable.
   * @param {string} locationId
   * @param {string} locationName
   * @param {{ address?: string, phone?: string, email?: string, opening_hours?: Record<string, string> }} parsedDetails
   * @returns {Record<string, string | number>}
   */
  #buildCartInfo(locationId, locationName, parsedDetails) {
    const openingHours = parsedDetails?.opening_hours || {};
    const info = {
      version: StoreSelectorDrawerComponent.CART_INFO_VERSION,
      store_id: locationId || '',
      store_name: locationName || '',
      store_address: parsedDetails?.address || '',
      store_phone: parsedDetails?.phone || '',
      store_email: parsedDetails?.email || '',
      open_monday: openingHours?.monday || '',
      open_tuesday: openingHours?.tuesday || '',
      open_wednesday: openingHours?.wednesday || '',
      open_thursday: openingHours?.thursday || '',
      open_friday: openingHours?.friday || '',
      open_saturday: openingHours?.saturday || '',
      open_sunday: openingHours?.sunday || '',
    };
    // Omit empty fields so `_cart_info` carries only populated values. Previously each
    // field was its own cart attribute and an empty value removed it, so consumers saw
    // a missing (nil) field. Keeping present-but-empty keys here would instead surface
    // as "" — which is truthy in Liquid — and make notification templates render empty
    // rows / broken tel:/mailto: links. Dropping the empty keys preserves the old
    // "absent when empty" semantics for the reader, emails and the pickup Function.
    return Object.fromEntries(Object.entries(info).filter(([, value]) => value !== ''));
  }

  /**
   * Forces checkout to rebuild its delivery options after a store change.
   *
   * A checkout keeps its own copy of the cart attributes from the moment it was
   * created. `/cart/update.js` never reaches that copy, so the pickup generator
   * keeps being asked with the previous store and offers a location the buyer has
   * abandoned. A change to the cart *lines* does reach it - `/cart/change.js`
   * bumps the cart version on every call, which is why the reconciler has to run
   * its changes sequentially - so one same-quantity write is enough to invalidate
   * the copy without altering the cart.
   *
   * Diagnosed on Maxi Zoo, MAXIZOO-832: cart said one store, checkout offered
   * another, and nudging a line quantity by hand was what made the right store
   * appear.
   */
  async #rebuildCheckout() {
    try {
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
      const line = cart?.items?.find((item) => item?.key);
      if (!line) return;

      const body = JSON.stringify({ id: line.key, quantity: line.quantity });
      await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    } catch (error) {
      console.error('[store-selector] Failed to rebuild checkout after store change', error);
    }
  }

  async #setCartAttributes(locationId, locationName, locationDetails) {
    const parsedDetails = this.#parseDetails(locationDetails);

    const attributes = {
      [StoreSelectorDrawerComponent.CART_INFO_KEY]: locationId ? JSON.stringify(this.#buildCartInfo(locationId, locationName, parsedDetails)) : '',
    };
    // Clear the legacy per-field attributes so the consolidated `_cart_info` is the
    // single source of truth (empty values remove the attribute from the cart).
    for (const key of StoreSelectorDrawerComponent.LEGACY_CART_ATTR_KEYS) {
      attributes[key] = '';
    }

    const config = fetchConfig('json', { body: JSON.stringify({ attributes }) });
    await fetch(Theme.routes.cart_update_url, config);
    await this.#rebuildCheckout();
    this.setAttribute(StoreSelectorDrawerComponent.CONNECTED_LOCATION_ID, locationId);
    this.setAttribute(StoreSelectorDrawerComponent.CONNECTED_LOCATION_NAME, locationName || '');
    if (locationId) {
      this.setAttribute(StoreSelectorDrawerComponent.CART_CONNECTED_LOCATION_ID, locationId);
    } else {
      this.removeAttribute(StoreSelectorDrawerComponent.CART_CONNECTED_LOCATION_ID);
    }
    if (locationName) {
      this.setAttribute(StoreSelectorDrawerComponent.CART_CONNECTED_LOCATION_NAME, locationName);
    } else {
      this.removeAttribute(StoreSelectorDrawerComponent.CART_CONNECTED_LOCATION_NAME);
    }
  }

  setLoading(isLoading) {
    this.#isSwitching = isLoading;
    const allButtons = this.querySelectorAll('[ref="selectStoreButton"], [ref="acceptButton"], [ref="cancelButton"]');

    allButtons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      if (isLoading) {
        if (!button.disabled) button.dataset.wasEnabled = 'true';
        button.disabled = true;

        // Only show spinner on the active button, others just get dimmed
        if (button === this.#activeButton || button.matches('[ref="acceptButton"]')) {
          button.classList.add('is-loading');
          button.setAttribute('aria-busy', 'true');
        } else {
          button.classList.add('is-inactive');
        }
      } else {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading', 'is-inactive');
        if (button.dataset.wasEnabled === 'true') {
          button.disabled = false;
          delete button.dataset.wasEnabled;
        }
      }
    });

    // Clear active button reference when done
    if (!isLoading) {
      this.#activeButton = null;
    }
  }

  #showPageLoadingOverlay() {
    if (this.#pageOverlay) return;

    const overlay = document.createElement('div');
    overlay.className = 'store-switch-overlay';
    overlay.innerHTML = `
      <div class="store-switch-overlay__spinner"></div>
    `;
    document.body.appendChild(overlay);
    this.#pageOverlay = overlay;
  }

  #hidePageLoadingOverlay() {
    if (this.#pageOverlay) {
      this.#pageOverlay.remove();
      this.#pageOverlay = null;
    }
  }

  #showErrorMessage(message) {
    // Show error in the drawer if it's open, otherwise use alert
    const confirmBox = this.querySelector('[ref="confirmBox"]');
    if (confirmBox && !confirmBox.hidden) {
      const body = confirmBox.querySelector('.store-selector-drawer-change-confirmation-body');
      if (body) {
        body.textContent = message;
        body.style.color = 'var(--color-error, #c00)';
      }
    } else {
      // Fallback to alert if drawer is closed
      alert(message);
    }
  }

  async #shouldConfirmSwitch(nextId, previousSelection) {
    if (!previousSelection?.id || previousSelection.id === nextId) return false;
    try {
      const cart = await this.#getCart();
      return Boolean(cart?.item_count > 0);
    } catch {
      return false;
    }
  }

  #showConfirmation(targetItem, nextSelection, previousSelection) {
    const box = this.querySelector('[ref="confirmBox"]');
    if (!(box instanceof HTMLElement)) return;
    this.#setPendingSelection(nextSelection, previousSelection);
    this.#hideConfirmation();
    targetItem.appendChild(box);
    box.hidden = false;
    const accept = box.querySelector('[ref="acceptButton"]');
    if (accept instanceof HTMLElement) accept.focus();
  }

  #hideConfirmation() {
    const box = this.querySelector('[ref="confirmBox"]');
    if (!(box instanceof HTMLElement)) return;
    box.hidden = true;
  }

  confirmCancel() {
    this.#hideConfirmation();
    this.#resetPendingSelection();
  }

  async confirmAccept() {
    const next = this.#pendingSelection;
    const previous = this.#pendingPrevious;
    this.#hideConfirmation();
    this.#resetPendingSelection();
    if (!next) return;
    await this.#commitSelection(next, previous);
  }

  async #clearCartOnStoreChange(nextId, previousSelection) {
    const currentCart = await this.#getCart();
    let nextCount = currentCart?.item_count || 0;

    if (currentCart?.item_count > 0 && previousSelection?.id && previousSelection.id !== nextId) {
      const clearResponse = await fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' });
      if (!clearResponse.ok) {
        throw new Error(`Failed to clear cart (${clearResponse.status})`);
      }
      nextCount = 0;
    }

    this.dispatchEvent(
      new CartUpdateEvent({}, this.id || 'store-selector-drawer', {
        source: 'store-selector-drawer',
        itemCount: nextCount,
      }),
    );
    return nextCount;
  }

  async #getCart() {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) {
      throw new Error(`Failed to fetch cart (${response.status})`);
    }
    return response.json();
  }

  /**
   * Filter list items by search query.
   * @param {InputEvent & { target: HTMLInputElement }} event
   */
  filterStores(event) {
    const query = event?.target?.value?.toLowerCase?.().trim?.() || '';
    const list = this.querySelector('[ref="storesList"]');
    if (!list) return;

    const items = list.querySelectorAll('.store-selector-drawer-item');
    const clearBtn = this.querySelector('.store-selector-drawer-clear-button');
    if (clearBtn instanceof HTMLElement) clearBtn.hidden = !query;
    const empty = this.querySelector('[ref="storesEmpty"]');

    if (!query) {
      items.forEach((li) => li instanceof HTMLElement && (li.hidden = false));
      if (empty instanceof HTMLElement) empty.hidden = true;
      return;
    }

    let visibleCount = 0;
    items.forEach((li) => {
      if (!(li instanceof HTMLElement)) return;
      const name = li.querySelector('.store-selector-drawer-item-name')?.textContent?.toLowerCase?.() || '';
      const address = li.querySelector('.store-selector-drawer-item-address')?.textContent?.toLowerCase?.() || '';
      const match = name.includes(query) || address.includes(query);
      li.hidden = !match;
      if (match) visibleCount++;
    });

    if (empty instanceof HTMLElement) empty.hidden = visibleCount !== 0;
  }

  #parseDetails(rawDetails) {
    if (!rawDetails) return {};
    if (typeof rawDetails === 'string') {
      const trimmed = rawDetails.trim();
      if (!trimmed) return {};
      try {
        return JSON.parse(trimmed);
      } catch {
        return {};
      }
    }
    if (typeof rawDetails === 'object') {
      return rawDetails;
    }
    return {};
  }

  clearSearch() {
    const input = this.querySelector('.store-selector-drawer-search-input');
    if (input instanceof HTMLInputElement) {
      input.value = '';
      const evt = new Event('input', { bubbles: true });
      input.dispatchEvent(evt);
      input.focus();
    }
  }
}

if (!customElements.get('store-selector-drawer')) {
  customElements.define('store-selector-drawer', StoreSelectorDrawerComponent);
}
